import { useState, useEffect, useMemo } from 'react';
import { C, t } from '../Dashboard/components/dashboardConstants';
import TalentContentContainer from '../layout/TalentContentContainer';
import SettingsMenu from './components/SettingsMenu';
import styles from './TalentSettingsPage.module.css';
import AccountSettings from './components/AccountSettings';
import NotificationSettings, { readNotificationFlags } from './components/NotificationSettings';
import PrivacySettings from './components/PrivacySettings';
import BillingSettings from './components/BillingSettings';
import { getMyTalentProfile, updateTalentProfile, setTalentVisibility, getAccountSettings, updateAccountSettings } from '../../../api';
import { getMe, updateMe } from '../../../api';

const PANEL_INFO = {
  '계정설정':   { title: '계정 설정',    subtitle: '배우 계정 정보와 보안 설정을 관리합니다.' },
  '알림설정':   { title: '알림 설정',    subtitle: '캐스팅 공고 및 메시지 알림을 관리합니다.' },
  '개인정보보호': { title: '개인정보 보호', subtitle: '프로필 공개 범위 및 데이터를 관리합니다.' },
  '결제및구독':  { title: '결제 및 구독', subtitle: '플랜 및 결제 수단을 관리합니다.' },
};

export default function TalentSettingsPage() {
  const [active, setActive] = useState('계정설정');
  const [profile, setProfile] = useState(null);
  const [user, setUser] = useState(null);
  // 알림·검색 노출 설정. undefined = 불러오는 중, null = 못 읽음
  const [account, setAccount] = useState(undefined);
  // 응답이 모두 도착했는지 — 그전에는 알림 값을 '모름'이 아니라 '불러오는 중'으로 본다
  const [accountLoaded, setAccountLoaded] = useState(false);

  useEffect(() => {
    Promise.allSettled([
      getMyTalentProfile().then(setProfile),
      getMe().then(setUser),
      getAccountSettings().then(setAccount).catch(() => setAccount(null)),
    ]).finally(() => setAccountLoaded(true));
  }, []);

  const handleSaveProfile = async (patch) => {
    await updateTalentProfile({ ...profile, ...patch });
    setProfile(prev => prev ? { ...prev, ...patch } : prev);
  };

  const handleSaveUser = async (patch) => {
    await updateMe(patch);
    setUser(prev => prev ? { ...prev, ...patch } : prev);
  };

  const handleVisibilityChange = async (visibility) => {
    try {
      await setTalentVisibility(visibility);
      setProfile(prev => prev ? { ...prev, visibility } : prev);
    } catch {}
  };

  const handleAccountSettingsChange = async (data) => {
    try {
      await updateAccountSettings(data);
    } catch {}
  };

  /**
   * 알림 설정 저장. 다른 설정과 달리 실패를 삼키지 않는다 —
   * 알림 메일 발송 기준이라, 저장이 안 됐는데 화면만 바뀌면 '껐는데 메일이 온다'가 된다.
   */
  const handleNotificationChange = async (patch) => {
    // 응답은 바뀐 뒤의 설정 전체다. 그대로 들고 있어야 다음 토글이 낡은 값을 기준으로 삼지 않는다.
    const saved = await updateAccountSettings(patch);
    setAccount(prev => ({ ...(prev ?? {}), ...patch, ...(saved ?? {}) }));
    return saved;
  };

  // undefined = 불러오는 중, null = 어디에서도 알림 값을 못 읽음.
  // 계정 설정 응답이 정답이고, 거기에 없는 필드만 사용자·프로필 응답에서 줍는다.
  // 매 렌더 새 객체를 만들면 자식이 그때마다 서버 값으로 되돌려, 저장 중인 토글이 튄다.
  const notificationFlags = useMemo(() => (
    accountLoaded
      ? readNotificationFlags(account, user, user?.settings, user?.notificationSettings, profile, profile?.accountSettings)
      : undefined
  ), [accountLoaded, account, user, profile]);

  const { title, subtitle } = PANEL_INFO[active];

  const renderPanel = () => {
    switch (active) {
      case '계정설정':
        return <AccountSettings profile={profile} user={user} onSaveProfile={handleSaveProfile} onSaveUser={handleSaveUser} />;
      case '알림설정':
        return <NotificationSettings initial={notificationFlags} onChange={handleNotificationChange} />;
      case '개인정보보호':
        return <PrivacySettings profile={profile} onVisibilityChange={handleVisibilityChange} onChange={handleAccountSettingsChange} />;
      case '결제및구독':
        return <BillingSettings />;
      default:
        return null;
    }
  };

  return (
    <div style={{ flex: 1, minWidth: 0, background: '#fcfcfd' }}>
      <TalentContentContainer>
        <div className={styles.header}>
          <div className={styles.headerInner}>
            <h1 className={styles.pageTitle}>계정 설정</h1>
            <p className={styles.pageSubtitle}>배우 계정 정보와 세부 설정을 관리합니다.</p>
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.layout}>
            <SettingsMenu active={active} onSelect={setActive} />
            <div className={styles.panelContent}>
              <div className={styles.panelHeader}>
                <span className={styles.panelTitle}>{title}</span>
                <span className={styles.panelSubtitle}>{subtitle}</span>
              </div>
              {renderPanel()}
            </div>
          </div>
        </div>
      </TalentContentContainer>
    </div>
  );
}
